import React, { useRef, useEffect } from 'react';
import { useNotifications } from '../context/NotificationsContext';
import { useLanguage } from '../context/LanguageContext';

function formatTimeAgo(timestamp, t) {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (minutes < 1) return t.now;
  if (minutes < 60) return t.minutesAgo.replace('{n}', minutes);
  if (hours < 24) return t.hoursAgo.replace('{n}', hours);
  return (days > 1 ? t.daysAgoPlural : t.daysAgo).replace('{n}', days);
}

const IconByType = ({ type }) => {
  const cls = 'w-5 h-5 flex-shrink-0';
  if (type === 'success') {
    return (
      <svg className={`${cls} text-emerald-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    );
  }
  if (type === 'warning') {
    return (
      <svg className={`${cls} text-amber-500`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    );
  }
  return (
    <svg className={`${cls} text-slate-500 dark:text-slate-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
};

const NOTIF_MSG_PT_TO_KEY = {
  'Simulação concluída com sucesso': 'notifSimulationComplete',
  'Simulação concluída': 'notifSimulationComplete',
  'Usuário clicou em link de phishing': 'notifPhishingClicked',
  'Usuário clicou no link de phishing': 'notifPhishingClicked',
  'Usuário compartilhou dados sensíveis em simulação de Engenharia Social': 'notifSEDataShared',
  'Dados sensíveis compartilhados em simulação de engenharia social': 'notifSEDataShared',
  'Nova vulnerabilidade detectada': 'notifVulnerabilityDetected',
  'Índice de Risco Humano atualizado': 'notifRiskUpdated',
  'Score de segurança atualizado': 'notifRiskUpdated',
};

export default function NotificationDropdown({ isOpen, onClose }) {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const { t } = useLanguage();

  const getNotificationMessage = (n) => {
    const key = n.messageKey || NOTIF_MSG_PT_TO_KEY[n.message];
    return (key && t[key]) ? t[key] : n.message;
  };
  const ref = useRef(null);

  useEffect(() => {
    if (isOpen && unreadCount > 0) markAllAsRead();
  }, [isOpen, unreadCount, markAllAsRead]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose?.();
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={ref}
      className="absolute right-0 top-full mt-2 w-80 max-h-[360px] bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden z-50 animate-fade-in-up"
    >
      <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{t.notifications}</h3>
        {unreadCount > 0 && (
          <span className="text-xs text-slate-500 dark:text-slate-400">{unreadCount} {unreadCount > 1 ? t.unreadPlural : t.unread}</span>
        )}
      </div>
      <div className="overflow-y-auto max-h-[300px]">
        {notifications.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">{t.noNotifications}</div>
        ) : (
          notifications.map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => markAsRead(n.id)}
              className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-slate-100 dark:hover:bg-slate-700/40 transition-colors border-b border-slate-200 dark:border-slate-700 last:border-0 ${
                !n.read ? 'bg-cyan-50 dark:bg-cyan-500/15' : ''
              }`}
            >
              <IconByType type={n.type} />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-700 dark:text-slate-300">{getNotificationMessage(n)}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{formatTimeAgo(n.time, t)}</p>
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
